<script lang="ts">
    import type { ColorVariation, OnColorChangePayload } from '@shared/types/types';

    interface Props {
        currentVariationId: string;
        detailPageLink: string;
        productLinkTitle: string;
        maxRenderedColors?: number;
        colors?: ColorVariation[];
        onColorChange: (args: OnColorChangePayload) => Promise<void>;
        onClickSurplusColors: (event: Event) => void;
    }

    let {
        currentVariationId,
        detailPageLink,
        productLinkTitle,
        maxRenderedColors = 5,
        colors = [],
        onColorChange,
        onClickSurplusColors,
    }: Props = $props();

    const totalNumberOfColors = $derived(colors?.length || 0);
    const surplusNumberOfColors = $derived(totalNumberOfColors - maxRenderedColors);
    const shownColors = $derived(colors.slice(0, maxRenderedColors));

    function handleColorChange(variationId: string, colorPosition: number) {
        if (variationId !== currentVariationId) {
            onColorChange({ variationId, amountColors: totalNumberOfColors, colorPosition });
        }
    }
</script>

{#if totalNumberOfColors > 1}
    <div class="reptile-color-thumbs" data-qa="reptile-color-thumbs">
        {#each shownColors as [variationId, color], index (variationId)}
            <div
                role="button"
                tabindex={0}
                aria-label={color.name}
                class="reptile-color-thumbs__thumb"
                data-qa="reptile-color-thumb"
                data-color-name={color.name}
                data-variation-id={variationId}
                data-selected={variationId === currentVariationId}
                onclick={() => {
                    handleColorChange(variationId, index);
                }}
                onkeypress={(e) => {
                    if (e.code === 'Enter' || e.code === 'Space') {
                        e.preventDefault();
                        handleColorChange(variationId, index);
                    }
                }}
            >
                <div
                    class={[
                        'reptile-color-thumbs__dot',
                        !color.hexCode ? color.baseColor || 'weiss' : '',
                        variationId == currentVariationId && 'reptile-color-thumbs__dot--selected',
                    ]}
                    style:background={color.hexCode ? `#${color.hexCode}` : undefined}
                ></div>
            </div>
        {/each}
        {#if surplusNumberOfColors > 0}
            <div class="reptile-color-thumbs__link">
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <oc-link-v2
                    base64-href={btoa(detailPageLink)}
                    inline="false"
                    variant="secondary"
                    class="oc-copy-50"
                    onclick={onClickSurplusColors}
                    data-link-type="color_tiles"
                >
                    <span title={productLinkTitle}>+{surplusNumberOfColors}</span>
                </oc-link-v2>
            </div>
        {/if}
    </div>
{/if}

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/_mixins/breakpoint' as mixins;
    @use '@shared/styles/mixins' as sharedMixins;

    .reptile-color-thumbs {
        display: flex;
        gap: tokens.$oc-semantic-spacing-absolute-50;
        margin: tokens.$oc-semantic-spacing-absolute-25 0;

        /*                                                                                                        */
        height: 100%;
        flex-flow: row nowrap;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-padding-left: tokens.$oc-semantic-spacing-absolute-25;

        @include mixins.breakpoint-from-l($to: false, $containerFirst: false) {
            flex-wrap: wrap;
        }

        &__thumb {
            cursor: pointer;
            scroll-snap-align: start;
            scroll-snap-stop: always;
            margin: 0;
        }

        &__dot {
            height: tokens.$oc-base-dimension-absolute-24;
            width: tokens.$oc-base-dimension-absolute-24;
            border: tokens.$oc-semantic-border-width-100 solid tokens.$oc-semantic-color-stroke-disabled;
            border-radius: 50%;

            @include sharedMixins.base-color-swatch;

            &:not(.reptile-color-thumbs__dot--selected):hover {
                border-color: tokens.$oc-semantic-color-text-default;
                box-shadow: inset 0 0 0 1px tokens.$oc-semantic-color-text-inverted;
            }

            &--selected {
                border-color: tokens.$oc-semantic-color-text-default;
                box-shadow: inset 0 0 0 2px tokens.$oc-semantic-color-text-inverted;
            }
        }

        &__link {
            min-width: tokens.$oc-base-dimension-absolute-24;
            min-height: tokens.$oc-base-dimension-absolute-24;
            color: tokens.$oc-base-color-gray-400;
            border: tokens.$oc-semantic-border-width-100 solid tokens.$oc-semantic-color-text-secondary;
            border-radius: 50%;
            text-align: center;
            text-decoration: none;
            cursor: pointer;
            display: flex;
            flex-wrap: wrap;
            place-content: center;

            &:hover {
                border-color: tokens.$oc-semantic-color-text-default;
                color: tokens.$oc-semantic-color-text-default;
            }
        }
    }
</style>
